import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { siteConfig } from '../../config/site';

const Wrap = styled.section`
  background: linear-gradient(135deg, var(--background-dark) 0%, var(--background-light) 100%);
  padding: 5rem 1rem 4rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 15% 25%, rgba(0, 153, 0, 0.14) 0%, transparent 55%),
      radial-gradient(circle at 85% 75%, rgba(0, 153, 0, 0.10) 0%, transparent 55%);
    pointer-events: none;
  }
`;

const Grid = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const Copy = styled.div`
  color: var(--text-color);
`;

const Eyebrow = styled.span`
  display: inline-block;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(0, 153, 0, 0.08);
  color: var(--primary-color);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 1.25rem;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  line-height: 1.05;
  margin: 0 0 1.25rem;
  color: var(--text-color);
  span { color: var(--primary-color); }
`;

const Lede = styled.p`
  font-size: 1.15rem;
  line-height: 1.7;
  color: var(--text-secondary);
  margin: 0 0 2rem;
  max-width: 560px;

  @media (max-width: 900px) { margin-left: auto; margin-right: auto; }
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  @media (max-width: 900px) { justify-content: center; }
`;

const PrimaryLink = styled.a`
  background: var(--primary-color);
  color: var(--text-color);
  padding: 0.95rem 1.9rem;
  border-radius: var(--border-radius);
  font-weight: 700;
  text-decoration: none;
  transition: transform 0.2s ease;
  &:hover { transform: translateY(-2px); }
`;

const GhostLink = styled(Link)`
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 0.95rem 1.9rem;
  border-radius: var(--border-radius);
  font-weight: 700;
  text-decoration: none;
  transition: background 0.2s ease;
  &:hover { background: rgba(0, 153, 0, 0.1); }
`;

const TrustRow = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 2.5rem;
  flex-wrap: wrap;
  @media (max-width: 900px) { justify-content: center; }
`;

const Stat = styled.div`
  strong {
    display: block;
    font-size: 1.75rem;
    font-weight: 800;
    color: var(--primary-color);
    line-height: 1.1;
  }
  span {
    font-size: 0.85rem;
    color: var(--text-secondary);
    letter-spacing: 0.02em;
  }
`;

const Media = styled.div`
  position: relative;
  aspect-ratio: 4/3;
  border-radius: 1.5rem;
  overflow: hidden;
  border: 1px solid rgba(0, 153, 0, 0.25);
  box-shadow: 0 20px 60px -20px rgba(0, 0, 0, 0.5);

  img {
    width: 100%; height: 100%;
    object-fit: cover; display: block;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(0, 153, 0, 0.08) 0%, transparent 60%);
    pointer-events: none;
  }
`;

const NewHero: React.FC = () => (
  <Wrap>
    <Grid>
      <Copy>
        <Eyebrow>{siteConfig.brand.name}</Eyebrow>
        <Title>
          Flash <span>USDT</span> across TRC-20, ERC-20 &amp; BEP-20 — safely, with support.
        </Title>
        <Lede>
          A licensed simulation tool for developers, traders, and teams who need
          realistic USDT balances for demos, wallet testing, and network validation —
          without touching mainnet funds.
        </Lede>
        <Actions>
          <PrimaryLink href={`https://wa.me/${siteConfig.contact.whatsapp}`} target="_blank" rel="noopener noreferrer">
            Buy on WhatsApp
          </PrimaryLink>
          <GhostLink to="/pricing">See pricing</GhostLink>
        </Actions>
        <TrustRow>
          <Stat><strong>$10M</strong><span>Daily flash cap</span></Stat>
          <Stat><strong>365d</strong><span>Live flash duration</span></Stat>
          <Stat><strong>50+</strong><span>Wallets supported</span></Stat>
          <Stat><strong>24/7</strong><span>WhatsApp support</span></Stat>
        </TrustRow>
      </Copy>
      <Media>
        <img src="/img/hero-tether.webp" alt="Tether (USDT) — Flash USDT Website" />
      </Media>
    </Grid>
  </Wrap>
);

export default NewHero;
